<template>
    <PostingModule
        :label="label"
        :data="data"
        :isLoading="loading()"
        :estimatedJobsLeft="estimatedJobsLeft"
        noToggles
        :smallView="smallView"
        :restrictHeight="restrictHeight"
    >
        <div v-if="data.length > 0" class="job-module-btn-group p-2">
            <BaseButton
                class="flex align-items-center justify-content-center w-100"
                variant="primary"
                :disabled="isOrgHierarchyLoading"
                @click="viewAllJobsInOrganization"
            >
                <Spinner v-if="isOrgHierarchyLoading" size="xs" class="mb-1" />
                {{
                    $t('companyDetail.jobModule.viewAllJobs', {
                        companyName: companyName,
                    })
                }}
            </BaseButton>

            <BaseButton class="w-100" variant="secondary" @click="viewJobsByOrgQuery">
                {{
                    $t('companyDetail.jobModule.viewRelevantJobs', {
                        companyName: companyName,
                    })
                }}
            </BaseButton>
        </div>

        <div slot="empty" class="pa-2 d-flex flex-column align-items-center justify-content-center">
            <img :src="noResult" class="no-result-image mt-2 mb-3" />

            <p class="text-center">
                {{
                    $t('companyDetail.jobModule.noPositions', {
                        companyName: companyName,
                    })
                }}
            </p>
        </div>
    </PostingModule>
</template>

<script>
import i18n from '~/localization/i18n';

// Services
import CompanyService from '~/services/CompanyService';

// Components
import PostingModule from '~/shared/postingmodule/PostingModule';

// Assets
import EmptySvg from '~/assets/img/empty.svg';

export default {
    name: 'JobModule',
    components: { PostingModule },
    props: {
        companyId: {
            type: [String, Number, Array],
            default: 0,
        },
        companyName: {
            type: [String, Number],
            default: '',
        },
        label: {
            type: String,
            optional: true,
            default: i18n.t('companyDetail.jobModule.defaultLabel'),
        },
        smallView: Boolean,
        restrictHeight: {
            type: Number,
            default: 0,
        },
        searchUsingCompanyName: Boolean,
        redirectUsingCompanyName: Boolean,
        redirectUsingQuery: Boolean,
    },
    data: () => ({
        noResult: EmptySvg,
        isOrgHierarchyLoading: false,
    }),
    computed: {
        customSearch() {
            return this.searchUsingCompanyName
                ? { query: this.companyName }
                : this.companyId
                ? { company_id: this.companyId }
                : {};
        },
        redirectValues() {
            //Company names must be allowed to include characters like '&'
            const encodedCompanyName = encodeURIComponent(this.companyName);
            return this.redirectUsingCompanyName
                ? ['?company_name=', encodedCompanyName]
                : this.redirectUsingQuery
                ? ['?query=', encodedCompanyName]
                : ['?company_id=', this.companyId];
        },
        companyJobs() {
            if (!this.companyId) {
                return [];
            }

            return this.$interfaces.posting.get({
                reactive: true,
                ...this.customSearch,
            });
        },

        estimatedJobsLeft() {
            if (this.companyJobs instanceof Array) {
                return;
            } else if (this.companyJobs.loading) {
                return;
            }

            return this.companyJobs.queryInfo.estimatedJobsLeft;
        },
        data() {
            return (this.companyJobs.data || []).slice(0, 5);
        },
    },
    methods: {
        loading() {
            if (!this.companyId) {
                return true;
            }

            return this.companyJobs.loading === undefined ? true : this.companyJobs.loading;
        },
        error() {
            return this.companyJobs.error;
        },
        viewJobsByOrgQuery() {
            let [key, value] = this.redirectValues;
            this.$emit('click');
            this.$router.push(this.$URLS.findjobs.concat(key, value));
        },
        viewAllJobsInOrganization() {
            this.isOrgHierarchyLoading = true;

            const companyId = Array.isArray(this.companyId)
                ? this.companyId.slice(-1)[0]
                : this.companyId;
            CompanyService.getOrganizationHierarchy(companyId, true)
                .then(hierarchy => {
                    let orgIds = Object.keys(hierarchy.result || {})
                        .map(id => `company_id=${id}`)
                        .join('&');
                    orgIds += '&page_size=100';
                    this.$router.push(this.$URLS.findjobs.concat('?', orgIds));
                })
                .finally(() => {
                    this.isOrgHierarchyLoading = false;
                });
        },
    },
};
</script>

<style scoped>
.no-result-image {
    max-width: 100px;
    height: 100px;
    max-height: 100px;
    object-fit: scale-down;
}
</style>

<style>
.job-module-btn-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    gap: 12px;
}

@media only screen and (max-width: 600px) {
    .job-module-btn-group {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr;
    }
}
</style>
